import React, { useEffect, useState, useMemo } from 'react';
import {
    Container,
    Grid,
    CircularProgress,
    useTheme
} from '@mui/material';
import { HistoryToggleOff } from '@mui/icons-material';
import analyticsService, { UserActivityWithDetails } from '../../../../services/analytics/AnalyticsService';
import AuditTable from './components/AuditTable';
import { PortalScreenHeader } from '../../../../components/PortalScreenHeader';
import { tablePreferencesStore } from '../../../../app/stores/TablePreferences';
import { AuditFilterConfig } from '../../../../components/AuditFilters';
import moment from 'moment';

const defaultFilters: AuditFilterConfig = {
    activityTypes: [],
    deviceTypes: [],
    userType: 'all',
    dateRange: 'all',
    screens: []
};

const Audit: React.FC = () => {
    const theme = useTheme();

    // Get preferences from store
    const {
        audit: auditPrefs,
        updateAuditFilters
    } = tablePreferencesStore();

    const [activities, setActivities] = useState<UserActivityWithDetails[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState<AuditFilterConfig>(auditPrefs.filters || defaultFilters);

    const fetchActivities = async () => {
        setLoading(true);

        try {
            const result = await analyticsService.getRecentActivities(250);
            if (result.success && result.data) {
                setActivities(result.data);
            }
        } catch (error: any) {
            console.error('Error fetching activities:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchActivities();
    }, []);

    // Get unique values for filter options
    const uniqueActivityTypes = useMemo(() => {
        const types = new Set<string>();
        activities.forEach(a => {
            if (a.activityType) types.add(a.activityType);
        });
        return Array.from(types).sort();
    }, [activities]);

    const uniqueDeviceTypes = useMemo(() => {
        const devices = new Set<string>();
        activities.forEach(a => {
            if (a.deviceType) devices.add(a.deviceType);
        });
        return Array.from(devices).sort();
    }, [activities]);

    const uniqueScreens = useMemo(() => {
        const screens = new Set<string>();
        activities.forEach(a => {
            if (a.screenName) screens.add(a.screenName);
        });
        return Array.from(screens).sort();
    }, [activities]);

    // Filter activities based on comprehensive filters and search term
    const filteredActivities = useMemo(() => {
        let filtered = activities;

        // Date Range Filter
        if (filters.dateRange !== 'all') {
            const now = moment();
            let cutoffTime: moment.Moment;

            switch (filters.dateRange) {
                case '1h':
                    cutoffTime = now.subtract(1, 'hour');
                    break;
                case '6h':
                    cutoffTime = now.subtract(6, 'hours');
                    break;
                case '24h':
                    cutoffTime = now.subtract(24, 'hours');
                    break;
                case '7d':
                    cutoffTime = now.subtract(7, 'days');
                    break;
                case '30d':
                    cutoffTime = now.subtract(30, 'days');
                    break;
                default:
                    cutoffTime = moment(0);
            }

            filtered = filtered.filter(a => moment(a.created).isAfter(cutoffTime));
        }

        // User Type Filter
        if (filters.userType === 'authenticated') {
            filtered = filtered.filter(a => a.userId != null);
        } else if (filters.userType === 'anonymous') {
            filtered = filtered.filter(a => a.userId == null);
        }

        // Activity Types Filter
        if (filters.activityTypes.length > 0) {
            filtered = filtered.filter(a => filters.activityTypes.includes(a.activityType || ''));
        }

        // Device Types Filter
        if (filters.deviceTypes.length > 0) {
            filtered = filtered.filter(a => filters.deviceTypes.includes(a.deviceType || ''));
        }

        // Screens Filter
        if (filters.screens.length > 0) {
            filtered = filtered.filter(a => filters.screens.includes(a.screenName || ''));
        }

        // Search Filter (applies across all fields)
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(activity =>
                activity.userName?.toLowerCase().includes(term) ||
                activity.anonymousId?.toLowerCase().includes(term) ||
                activity.userEmail?.toLowerCase().includes(term) ||
                activity.activityType?.toLowerCase().includes(term) ||
                activity.screenName?.toLowerCase().includes(term) ||
                activity.screenPath?.toLowerCase().includes(term) ||
                activity.deviceType?.toLowerCase().includes(term) ||
                activity.browser?.toLowerCase().includes(term)
            );
        }

        return filtered;
    }, [activities, filters, searchTerm]);

    return (
        <Container
            component='main'
            maxWidth='xl'
            sx={{
                height: '100%',
                paddingX: { xs: 0, md: 2 },
            }}>
            {
                loading && activities.length === 0 ?
                    <Grid container justifyContent='center' mt={20}>
                        <Grid item>
                            <CircularProgress
                                size={40}
                                sx={{ color: theme.palette.primary.main }}
                            />
                        </Grid>
                    </Grid>
                    :
                    <>
                        <PortalScreenHeader
                            title='Audit Log'
                            subtitle='Detailed activity history and tracking logs'
                            icon={<HistoryToggleOff />}
                        />

                        <AuditTable
                            activities={filteredActivities}
                            loading={loading}
                            onRefresh={fetchActivities}
                            searchTerm={searchTerm}
                            onSearchChange={(value) => setSearchTerm(value)}
                            filters={filters}
                            onFiltersChange={(newFilters) => {
                                setFilters(newFilters);
                                updateAuditFilters(newFilters);
                            }}
                            availableActivityTypes={uniqueActivityTypes}
                            availableDeviceTypes={uniqueDeviceTypes}
                            availableScreens={uniqueScreens}
                        />
                    </>
            }
        </Container>
    );
};

export default Audit;

